/**
 * useLocale hook - React hook for i18n
 *
 * Provides locale state and translation function
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import { Locale, getLocale, setStoredLocale, supportedLocales } from '../lib/i18n'
import { translations } from '../lib/translations'

type TranslationValue = string | Record<Locale, string> | Record<string, unknown>

export function useLocale() {
  const [locale, setLocaleState] = useState<Locale>(getLocale)

  // Update HTML lang attribute when locale changes
  useEffect(() => {
    document.documentElement.lang = locale
  }, [locale])

  // Set locale and persist
  const setLocale = useCallback((newLocale: Locale) => {
    if (supportedLocales.includes(newLocale)) {
      setLocaleState(newLocale)
      setStoredLocale(newLocale)
    }
  }, [])

  // Toggle between languages
  const toggleLocale = useCallback(() => {
    const currentIndex = supportedLocales.indexOf(locale)
    const nextIndex = (currentIndex + 1) % supportedLocales.length
    setLocale(supportedLocales[nextIndex])
  }, [locale, setLocale])

  // Translation function
  const t = useCallback(<
    S extends keyof typeof translations,
    K extends keyof (typeof translations)[S]
  >(section: S, key: K): string => {
    const translation = translations[section][key] as TranslationValue
    if (typeof translation === 'object' && translation !== null && 'en' in translation) {
      return (translation as Record<Locale, string>)[locale] || (translation as Record<Locale, string>).en
    }
    return key as string
  }, [locale])

  // Deep translation for nested objects (3 levels)
  const tNested = useCallback(<
    S extends keyof typeof translations,
    K extends keyof (typeof translations)[S],
    NK extends string
  >(section: S, key: K, nestedKey: NK): string => {
    const obj = translations[section][key] as Record<string, unknown>
    if (obj && typeof obj === 'object') {
      const nested = obj[nestedKey] as TranslationValue
      if (nested && typeof nested === 'object' && 'en' in nested) {
        return (nested as Record<Locale, string>)[locale] || (nested as Record<Locale, string>).en
      }
    }
    return nestedKey
  }, [locale])

  // Deep translation for deeply nested objects (4 levels)
  const tDeep = useCallback(<
    S extends keyof typeof translations,
    K extends keyof (typeof translations)[S],
    NK extends string,
    DNK extends string
  >(section: S, key: K, nestedKey: NK, deepNestedKey: DNK): string => {
    const obj = translations[section][key] as Record<string, unknown>
    if (obj && typeof obj === 'object') {
      const nested = obj[nestedKey] as Record<string, unknown>
      if (nested && typeof nested === 'object') {
        const deep = nested[deepNestedKey] as TranslationValue
        if (deep && typeof deep === 'object' && 'en' in deep) {
          return (deep as Record<Locale, string>)[locale] || (deep as Record<Locale, string>).en
        }
      }
    }
    return deepNestedKey
  }, [locale])

  // Very deep translation for 5-level nested objects
  const tDeeper = useCallback(<
    S extends keyof typeof translations,
    K extends keyof (typeof translations)[S],
    NK extends string,
    DNK extends string,
    VDK extends string
  >(section: S, key: K, nestedKey: NK, deepNestedKey: DNK, veryDeepKey: VDK): string => {
    const obj = translations[section][key] as Record<string, unknown>
    if (obj && typeof obj === 'object') {
      const nested = obj[nestedKey] as Record<string, unknown>
      if (nested && typeof nested === 'object') {
        const deep = nested[deepNestedKey] as Record<string, unknown>
        if (deep && typeof deep === 'object') {
          const veryDeep = deep[veryDeepKey] as TranslationValue
          if (veryDeep && typeof veryDeep === 'object' && 'en' in veryDeep) {
            return (veryDeep as Record<Locale, string>)[locale] || (veryDeep as Record<Locale, string>).en
          }
        }
      }
    }
    return veryDeepKey
  }, [locale])

  return useMemo(() => ({
    locale,
    setLocale,
    toggleLocale,
    t,
    tNested,
    tDeep,
    tDeeper,
    isSpanish: locale === 'es',
    isEnglish: locale === 'en',
  }), [locale, setLocale, toggleLocale, t, tNested, tDeep, tDeeper])
}

export type UseLocaleReturn = ReturnType<typeof useLocale>
